import { forwardRef, useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDemo } from '@/contexts/DemoContext';
import { DemoConversionCta } from './DemoConversionCta';
import { DemoRewardMilestone } from './DemoRewardMilestone';

const MILESTONES = [
  { days: 7, key: 'reward7days' },
  { days: 14, key: 'reward14days' },
  { days: 30, key: 'reward30days' },
  { days: 60, key: 'reward60days' },
  { days: 100, key: 'reward100days' },
  { days: 182, key: 'reward6months' },
  { days: 300, key: 'reward300days' },
  { days: 365, key: 'reward365days' },
] as const;

interface DemoRewardsSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const DemoRewardsSection = forwardRef<HTMLElement, DemoRewardsSectionProps>(
  function DemoRewardsSection({ onModalOpen, onModalClose }, ref) {
    const { t } = useLanguage();
    const { demoLongestStreak } = useDemo();

    // Demo: first milestone (7 days) is unlocked
    const demoStreak = Math.max(demoLongestStreak, 8); // Ensure 7-day milestone is unlocked
    
    // Find the next milestone
    const nextMilestoneDays = MILESTONES.find(m => demoStreak < m.days)?.days;

    return (
      <section ref={ref} id="rewards" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('rewardsTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('rewardsSubtitle')}
          </p>
        </div>

        {/* Milestones */}
        <div className="px-6 space-y-3 max-w-lg mx-auto">
          {MILESTONES.map((milestone) => (
            <DemoRewardMilestone
              key={milestone.days}
              days={milestone.days}
              translationKey={milestone.key}
              longestStreak={demoStreak}
              isNextMilestone={milestone.days === nextMilestoneDays}
              onModalOpen={onModalOpen}
              onModalClose={onModalClose}
            />
          ))}
        </div>

        {/* Conversion CTA */}
        <DemoConversionCta variant="inline" className="mt-6" />
      </section>
    );
  }
);
